import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useIsWebView, useToken, useWebViewLoading, useDoctorRouteGuard } from '../../../shared/hooks';
import { DoctorRouteDialog } from '../../../shared/components/DoctorRouteDialog';
import { appBridge } from '../../../shared/services/bridge';
import { RECEIPT_NOTICE_SELECT_LINES } from '../constants/receiptTexts';
import { usePaymentMethod, type PaymentMethodMode, ERROR_CODE_PROCESSED_SYSTEM } from '../hooks/usePaymentMethod';
import ReceiptNotice from './ReceiptNotice';
import type { PaymentOption } from '../types';

import './PaymentMethod.css';

/**
 * 支払方法選択画面
 *
 * 2 つの mode で使い分ける:
 * - `mode=order`: 注文サイン前の事前選択
 *   - 選択 → POST /payment/set → /inquiries/:id/sign へ
 * - `mode=change`: 支払方法変更（支払情報入力画面から呼ばれる）
 *   - 選択 → POST /payment/set → 新メソッドに応じた画面へ
 *
 * GET /payment で既存の選択を復元する（戻る操作でも選択が保持される）。
 */
const PaymentMethod: React.FC = () => {
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = useToken();

  // mode クエリパラメータで注文前/変更を分岐。デフォルトは `change`（既存経路との互換）
  const modeParam = searchParams.get('mode');
  const mode: PaymentMethodMode = modeParam === 'order' ? 'order' : 'change';

  const inquiryId = id ?? '';

  const { shouldBlock, handleConfirm: handleGuardConfirm } = useDoctorRouteGuard(id, token);

  const {
    selectedMethod,
    isLoading,
    isSubmitting,
    canProceed,
    error: paymentError,
    selectMethod,
    submit,
    getOriginalPaymentPath,
    resetToOriginal,
    availableMethods,
  } = usePaymentMethod(mode, inquiryId, token);

  // PPTX指示(0506) スライド11:
  // 支払い手続きから支払方法変更経由でこの画面に遷移した場合（mode='change'）は
  // ヘッダの「＜」を非表示にする（中途状態への戻りを防ぐ）。
  useEffect(() => {
    if (isWebView && mode === 'change') {
      appBridge.hideBackButton();
    }
  }, [isWebView, mode]);

  // PPTX指示(0506) スライド11:
  // API側 PROCESSED_SYSTEM_ERROR（ドクター端末で進行中）のとき専用UIに切替える。
  const isProcessedSystemError = paymentError?.code === ERROR_CODE_PROCESSED_SYSTEM;
  const displayErrorMessage = isProcessedSystemError
    ? '別の端末と手続きが重複しているため、アプリから手続きを行えません。'
    : paymentError?.message;

  // データ取得中は Flutter 側のローディングオーバーレイに任せる
  const contentRef = useWebViewLoading(isLoading);

  const handleNext = async () => {
    if (!canProceed || !inquiryId) return;
    const nextPath = await submit(inquiryId);
    if (nextPath) {
      // mode=change で選択変更した場合は履歴を置換し、元画面に戻れないようにする
      // （履歴に支払方法選択が残ると UX が混乱するため）
      navigate(nextPath, mode === 'change' ? { replace: true } : undefined);
    }
  };

  // PPTX指示(0506) スライド11:
  // 「元の支払選択に戻る」ボタン。
  // ① 元の支払選択に応じた画面（カード→カード情報入力、現金/請求書→支払内容確認、後払い→後払い確認）に遷移
  // ② この画面の情報（選択・請求先情報・エラー）をリセット
  const handleBackToOriginalPayment = () => {
    if (!inquiryId) return;
    const path = getOriginalPaymentPath(inquiryId);
    resetToOriginal();
    if (path) {
      navigate(path, { replace: true });
    }
  };

  // 利用可能な支払方法のコードリストに含まれない選択肢は disabled にする。
  // 取得前 / 取得失敗時は availableMethods=[] となり全 disabled（ドクター usePaymentPage 踏襲）。
  const paymentOptions: {
    value: PaymentOption;
    label: string;
    icon: string;
    alt: string;
    disabled: boolean;
  }[] = [
    { value: 'credit', label: 'クレジット決済', icon: '/assets/images/Card.png', alt: 'クレジットカード', disabled: !availableMethods.includes('credit') },
    { value: 'cash', label: '現金決済', icon: '/assets/images/Cash.png', alt: '現金', disabled: !availableMethods.includes('cash') },
    { value: 'bill', label: '請求書払い', icon: '/assets/images/Cash.png', alt: '請求書', disabled: !availableMethods.includes('bill') },
    { value: 'postpaid', label: '後払い決済', icon: '/assets/images/PayLater.png', alt: '後払い', disabled: !availableMethods.includes('postpaid') },
  ];

  // mode に応じた「次へ」ボタンの文言（PPTX指示 0506 スライド11: change 時の文言を「次へ進む」に変更）
  const nextButtonText = mode === 'order' ? '注文サインへ進む' : '次へ進む';

  // PPTX指示(0506) スライド3:
  // 注文手続きからの遷移時はタイトル「注文手続き」/ 説明文「ご希望のお支払い方法を選択してください。\nお支払い手続きは、修繕作業完了後に行います。」
  // 支払い手続きからの遷移時は既存表記を維持
  const screenTitle = mode === 'order' ? '注文手続き' : 'お支払い方法の選択';
  const screenSubtitle = mode === 'order'
    ? 'ご希望のお支払い方法を選択してください。\nお支払い手続きは、修繕作業完了後に行います。'
    : 'お支払い方法を選択してください。';

  if (isLoading) {
    // ローディング中は Flutter オーバーレイを表示するため空コンテナを返す
    return <div className="payment-container" />;
  }

  // ドクタールートに切り替わっていた場合は Flutter ダッシュボードへ強制復帰
  if (shouldBlock) {
    return <DoctorRouteDialog onConfirm={handleGuardConfirm} />;
  }

  return (
    <div ref={contentRef} className="payment-container">
      <main className="payment-content">
        <h2 className="content-title">{screenTitle}</h2>
        <p className="content-subtitle" style={{ whiteSpace: 'pre-line' }}>{screenSubtitle}</p>

        {displayErrorMessage && <p className="error-text">{displayErrorMessage}</p>}

        <div className="payment-options">
          {paymentOptions.map((option) => (
            <div
              key={option.value}
              className={`option-row ${selectedMethod === option.value ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
              onClick={() => !option.disabled && selectMethod(option.value)}
            >
              <div className="radio-button">
                <div className="radio-dot"></div>
              </div>
              <span className="option-label">{option.label}</span>
              <img src={option.icon} alt={option.alt} className="option-icon" />
            </div>
          ))}
        </div>

        {/* 領収書発行仕様変更（課題表381 / PDF P6）:
            支払方法選択（order）・支払方法変更（change）の両方に同一の説明を表示する。 */}
        <ReceiptNotice lines={RECEIPT_NOTICE_SELECT_LINES} />

        {/* 請求書払いの請求先情報は確認画面（CashPaymentConfirm）で入力する。
            この画面では mode に関わらず入力欄を表示しない（二重入力バグ修正）。 */}
      </main>

      <footer className="payment-footer">
        <button
          className="button-primary"
          disabled={!canProceed || isSubmitting}
          onClick={handleNext}
        >
          {isSubmitting ? '処理中...' : nextButtonText}
        </button>
        {/* PPTX指示(0506) スライド11: 端末重複エラー時のみ「元の支払選択に戻る」を表示。
            元の選択が存在しない場合は遷移先が決まらないので非表示にする。 */}
        {isProcessedSystemError && mode === 'change' && getOriginalPaymentPath(inquiryId) && (
          <button className="button-secondary" onClick={handleBackToOriginalPayment}>
            元の支払選択に戻る
          </button>
        )}
        {!isWebView && (
          <button className="button-secondary" onClick={() => navigate(-1)}>
            戻る
          </button>
        )}
      </footer>
    </div>
  );
};

export default PaymentMethod;
